import { useState, useRef, useEffect } from 'react';
import { useVantaClouds } from '../hooks/useVantaClouds';
import './LoginPage.css';

interface LoginPageProps {
  onLoginSuccess: (email: string) => void;
}

interface FieldErrors {
  email?: string;
  password?: string;
  form?: string;
}

export const LoginPage = ({ onLoginSuccess }: LoginPageProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const isFormValid = email.trim().length > 0 && password.trim().length > 0 && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (res.ok) {
        const { email: confirmedEmail } = await res.json() as { email: string };

        // Fire-and-forget login tracking
        fetch('/api/tracking/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify([{
            email: confirmedEmail,
            eventType: 'login',
            timestamp: new Date().toISOString(),
            payload: { userAgent: navigator.userAgent.slice(0, 512) },
          }]),
        }).catch(() => { /* ponytail: fire-and-forget */ });

        onLoginSuccess(confirmedEmail);
        return;
      }

      // 422 = malformed input, 401 = wrong credentials — both get a generic message
      // to avoid leaking which field is wrong
      if (res.status === 422) {
        const body = await res.json() as { field?: string; message?: string };
        setErrors({ [body.field ?? 'form']: body.message ?? 'Invalid input' });
      } else {
        // 401 or unexpected: generic message — no hint about which field failed
        setErrors({ form: 'Invalid email or password' });

        // Track failed attempt (fire-and-forget)
        fetch('/api/tracking/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify([{
            email: email.trim().toLowerCase(),
            eventType: 'login_failed',
            timestamp: new Date().toISOString(),
            payload: {
              userAgent: navigator.userAgent.slice(0, 512),
              reason: 'invalid_credentials',
            },
          }]),
        }).catch(() => { /* ponytail: fire-and-forget */ });
      }
    } catch {
      setErrors({ form: 'Unable to reach the server. Check your connection.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const { containerRef } = useVantaClouds();

  return (
    <div className="login-page">
      <div className="login-vanta-bg" ref={containerRef} aria-hidden="true" />
      <form className="login-form" onSubmit={handleSubmit} noValidate>
        <div className="login-wordmark">
          <svg className="login-logo" width="48" height="48" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 3 L20 16 L16 29 L12 16 Z" stroke="currentColor" strokeWidth="1.8" fill="none"/>
            <path d="M3 16 L16 12 L29 16 L16 20 Z" stroke="currentColor" strokeWidth="1.8" fill="none"/>
          </svg>
          <span className="login-prefix">DTN</span>
          <h1 className="login-title">Station Explorer</h1>
        </div>

        {errors.form && (
          <p className="login-error login-error--form" role="alert">{errors.form}</p>
        )}

        <div className="login-field">
          <input
            ref={emailRef}
            id="login-email"
            className="login-input"
            type="email"
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={isSubmitting}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
          />
          <span className="login-bar" />
          <label htmlFor="login-email">Email</label>
          {errors.email && (
            <p id="login-email-error" className="login-error" role="alert">{errors.email}</p>
          )}
        </div>

        <div className="login-field">
          <input
            id="login-password"
            className="login-input"
            type="password"
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={isSubmitting}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'login-password-error' : undefined}
          />
          <span className="login-bar" />
          <label htmlFor="login-password">Password</label>
          {errors.password && (
            <p id="login-password-error" className="login-error" role="alert">{errors.password}</p>
          )}
        </div>

        <button
          type="submit"
          className="login-submit"
          disabled={!isFormValid}
        >
          {isSubmitting ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
};
